import { FieldsRenderer } from '@neo/blocks/forms/fields-renderer'
import type { GenericFormType } from '@neo/blocks/forms/types'
import { PlayPauseIcon } from '@neo/primitives/audio-player'
import { Loader2 } from 'lucide-react'
import { useCallback, useState } from 'react'
import type { AgentSettingsFormValues } from '../../config/base.config'
import { voiceConfigSchema } from '../../config/validation.config'
import { useTextToSpeechMutation, useVoicesQuery } from '../../queries'
import type { RVoiceWithModelAndProvider } from '../types'

export const VoiceForm = ({
  value,
  arrayIndex,
  form,
}: {
  value: { word: string; guide: string }
  arrayIndex: number
  form: GenericFormType<AgentSettingsFormValues>
}) => {
  const voiceConfig = form.state.values.voice_ai_config?.text_to_speech
  const language = form.state.values.voice_ai_config?.language
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const textToSpeech = useTextToSpeechMutation()
  const { data: voices } = useVoicesQuery(language || '')

  const displayPlayIcon = !voiceConfig?.voice || !value.guide?.trim()?.length

  // Get accent from selected voice
  const getAccentModelProviderFromVoice = useCallback(() => {
    if (!voiceConfig?.voice || !voices) return { accent: '', model: '', provider: '' }

    const selectedVoice = voices?.find(
      (v: RVoiceWithModelAndProvider) => v.id === voiceConfig?.voice,
    )
    return {
      accent: selectedVoice?.meta?.accent || '',
      model: selectedVoice?.model || '',
      provider: selectedVoice?.provider || '',
    }
  }, [voiceConfig?.voice, voices])

  const handlePlayPreview = useCallback(async () => {
    if (!voiceConfig?.voice || !value.guide?.trim()) return

    try {
      setIsLoading(true)
      setIsPlaying(true)
      const { accent, model, provider } = getAccentModelProviderFromVoice()

      const result = await textToSpeech.mutateAsync({
        text: value.guide.trim(),
        voice_id: voiceConfig.voice,
        model_id: model,
        provider_id: provider,
        language_code: accent,
      })
      const audio = new Audio(result.pronunciation_preview_url)
      audio.onended = () => setIsPlaying(false)
      await audio.play()
    } catch {
      setIsPlaying(false)
    } finally {
      setIsLoading(false)
    }
  }, [textToSpeech, value.guide, voiceConfig?.voice, getAccentModelProviderFromVoice])

  return (
    <div className="w-full max-w-2xl">
      <div className="relative rounded-md border border-border bg-bg2 p-4">
        <h3 className="flex items-center text-p-500 text-foreground">
          {(() => {
            if (displayPlayIcon) {
              return <PlayPauseIcon className="mr-2 size-7 shrink-0" disabled playing={false} />
            }
            if (isLoading) {
              return (
                <div className="mr-2 flex size-7 shrink-0 items-center justify-center">
                  <Loader2 className="size-5 animate-spin text-brand-text" />
                </div>
              )
            }
            return (
              <PlayPauseIcon
                className="mr-2 size-7 shrink-0"
                onClick={(e) => {
                  e.stopPropagation()
                  if (isPlaying) setIsPlaying(false)
                  else handlePlayPreview()
                }}
                playing={isPlaying}
              />
            )
          })()}
          Pronunciation {arrayIndex + 1}
        </h3>
        <div className="space-y-4 mt-4">
          <FieldsRenderer
            form={form}
            baseName={`voice_ai_config.text_to_speech.pronunciation[${arrayIndex}]`}
            arrayIndex={arrayIndex}
            fields={[
              {
                name: `voice_ai_config.text_to_speech.pronunciation[0].word`,
                componentType: 'input',
                label: 'Word',
                inputProps: {
                  placeholder: 'Enter word/phrase, ex: Plivo',
                },
                zodValidation: voiceConfigSchema.shape.pronunciation.element.shape.word,
              },
              {
                name: `voice_ai_config.text_to_speech.pronunciation[0].guide`,
                componentType: 'input',
                label: 'Pronunciation guide',
                zodValidation: voiceConfigSchema.shape.pronunciation.element.shape.guide,
                inputProps: {
                  placeholder: 'Enter pronunciation guide, ex: Plee-voh',
                },
              },
            ]}
          />
        </div>
      </div>
    </div>
  )
}
